import React, { useEffect, useState } from "react";
import { apiRequest } from "../helpers/api";
import { AccountProfileSkeleton } from "../components/skeleton/PageSkeletons";
import { getLoggedInUser, getToken, setAuthData } from "../helpers/authHelper";
import { showError, ToastFeedback } from "../components/ToastNotifications";
import "./AccountProfile.css";

const emptyProfile = {
  firstName: "",
  lastName: "",
  fullName: "",
  email: "",
  phone: "",
  designation: ""
};

const emptyPassword = { currentPassword: "", newPassword: "", confirmNewPassword: "" };

const utf8Length = (value) => {
  let length = 0;
  for (const character of value) {
    const codePoint = character.codePointAt(0);
    length += codePoint <= 0x7f ? 1 : codePoint <= 0x7ff ? 2 : codePoint <= 0xffff ? 3 : 4;
  }
  return length;
};

const validateProfile = (profile) => {
  if (!profile.firstName.trim() || !profile.lastName.trim()) return "First name and last name are required.";
  if (profile.firstName.trim().length > 80 || profile.lastName.trim().length > 80) return "Names must be 80 characters or fewer.";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profile.email.trim()) || profile.email.trim().length > 254) return "Enter a valid email address.";
  const phone = profile.phone.trim();
  if (phone && (!/^\+?[0-9().\-\s]+$/.test(phone) || phone.replace(/\D/g, "").length < 7 || phone.replace(/\D/g, "").length > 15)) {
    return "Enter a valid phone number with 7 to 15 digits.";
  }
  if (profile.designation.trim().length > 255) return "Designation must be 255 characters or fewer.";
  return "";
};

const validatePassword = (password) => {
  if (!password.currentPassword || !password.newPassword || !password.confirmNewPassword) return "Complete all password fields.";
  if (password.newPassword.length < 10 || utf8Length(password.newPassword) > 72 || !/[A-Za-z]/.test(password.newPassword) || !/\d/.test(password.newPassword)) {
    return "New password must be 10 to 72 characters and include a letter and a number.";
  }
  if (password.newPassword !== password.confirmNewPassword) return "New password and confirmation do not match.";
  if (password.newPassword === password.currentPassword) return "New password must be different from your current password.";
  return "";
};

const PasswordField = ({ id, label, value, onChange, visible, onToggle, autoComplete }) => <div className="account-profile-field account-password-field">
  <label className="form-label" htmlFor={id}>{label}</label>
  <div className="account-password-control">
    <input
      id={id}
      className="form-control"
      type={visible ? "text" : "password"}
      maxLength={72}
      autoComplete={autoComplete}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      required
    />
    <button className="account-password-toggle" type="button" onClick={onToggle} aria-label={`${visible ? "Hide" : "Show"} ${label.toLowerCase()}`}>
      <i className={`ti ${visible ? "ti-eye-off" : "ti-eye"}`} aria-hidden="true" />
    </button>
  </div>
</div>;

const AccountProfile = () => {
  const [profile, setProfile] = useState(emptyProfile);
  const [savedProfile, setSavedProfile] = useState(emptyProfile);
  const [password, setPassword] = useState(emptyPassword);
  const [passwordVisibility, setPasswordVisibility] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [profileError, setProfileError] = useState("");
  const [profileSuccess, setProfileSuccess] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");

  useEffect(() => {
    let current = true;
    apiRequest("/api/auth/profile")
      .then(({ user }) => {
        if (!current) return;
        const loaded = { ...emptyProfile, ...user };
        setProfile(loaded);
        setSavedProfile(loaded);
      })
      .catch((error) => { if (current) showError(error.message); })
      .finally(() => { if (current) setLoading(false); });
    return () => { current = false; };
  }, []);

  const updateProfileField = (field, value) => setProfile((current) => ({ ...current, [field]: value }));
  const resetProfile = () => {
    setProfile({ ...savedProfile });
    setProfileError("");
    setProfileSuccess("");
  };

  const saveProfile = async (event) => {
    event.preventDefault();
    setProfileError("");
    setProfileSuccess("");
    const validationError = validateProfile(profile);
    if (validationError) {
      setProfileError(validationError);
      return;
    }
    setSavingProfile(true);
    try {
      const result = await apiRequest("/api/auth/profile", {
        method: "PUT",
        body: JSON.stringify({
          firstName: profile.firstName,
          lastName: profile.lastName,
          email: profile.email,
          phone: profile.phone,
          designation: profile.designation
        })
      });
      const updated = { ...emptyProfile, ...result.user };
      setProfile(updated);
      setSavedProfile(updated);
      setAuthData(getToken(), { ...getLoggedInUser(), email: updated.email, firstName: updated.firstName, lastName: updated.lastName });
      setProfileSuccess(result.message);
    } catch (error) {
      showError(error.message);
    } finally {
      setSavingProfile(false);
    }
  };

  const savePassword = async (event) => {
    event.preventDefault();
    setPasswordError("");
    setPasswordSuccess("");
    const validationError = validatePassword(password);
    if (validationError) {
      setPasswordError(validationError);
      return;
    }
    setSavingPassword(true);
    try {
      const result = await apiRequest("/api/auth/password", {
        method: "PUT",
        body: JSON.stringify(password)
      });
      setAuthData(result.token, getLoggedInUser());
      setPassword(emptyPassword);
      setPasswordVisibility({});
      setPasswordSuccess(result.message);
    } catch (error) {
      showError(error.message);
    } finally {
      setSavingPassword(false);
    }
  };

  if (loading) return <section className="account-profile-page"><AccountProfileSkeleton /></section>;

  return <section className="account-profile-page">
    <header className="account-profile-header">
      <div><p className="account-profile-eyebrow">ACCOUNT</p><h1>Personal information</h1><p>Manage your personal details and account security.</p></div>
    </header>

    <section className="account-profile-card">
      <header className="account-profile-card-header"><div><h2>Profile information</h2><p>Update the information associated with your account.</p></div></header>
      {profileError && <div className="account-profile-alert is-error" role="alert">{profileError}</div>}
      {profileSuccess && <ToastFeedback type="success" message={profileSuccess} />}
      <form onSubmit={saveProfile} noValidate>
        <div className="account-profile-grid">
          <div className="account-profile-field">
            <label className="form-label" htmlFor="account-first-name">First name</label>
            <input id="account-first-name" className="form-control" autoComplete="given-name" maxLength={80} value={profile.firstName} onChange={(event) => updateProfileField("firstName", event.target.value)} required />
          </div>
          <div className="account-profile-field">
            <label className="form-label" htmlFor="account-last-name">Last name</label>
            <input id="account-last-name" className="form-control" autoComplete="family-name" maxLength={80} value={profile.lastName} onChange={(event) => updateProfileField("lastName", event.target.value)} required />
          </div>
          <div className="account-profile-field">
            <label className="form-label" htmlFor="account-full-name">Full name</label>
            <input id="account-full-name" className="form-control" value={[profile.firstName, profile.lastName].filter(Boolean).join(" ")} readOnly />
            <span className="account-profile-help">Full name is derived from your first and last name.</span>
          </div>
          <div className="account-profile-field">
            <label className="form-label" htmlFor="account-email">Email address</label>
            <input id="account-email" className="form-control" type="email" autoComplete="email" maxLength={254} value={profile.email} onChange={(event) => updateProfileField("email", event.target.value)} required />
          </div>
          <div className="account-profile-field">
            <label className="form-label" htmlFor="account-phone">Phone number</label>
            <input id="account-phone" className="form-control" type="tel" autoComplete="tel" maxLength={40} value={profile.phone} onChange={(event) => updateProfileField("phone", event.target.value)} />
          </div>
          <div className="account-profile-field">
            <label className="form-label" htmlFor="account-designation">Designation</label>
            <input id="account-designation" className="form-control" autoComplete="organization-title" maxLength={255} value={profile.designation} onChange={(event) => updateProfileField("designation", event.target.value)} />
          </div>
        </div>
        <footer className="account-profile-actions">
          <button type="button" className="account-profile-button is-secondary" onClick={resetProfile} disabled={savingProfile}>Cancel</button>
          <button type="submit" className="account-profile-button is-primary" disabled={savingProfile}>{savingProfile ? <><span className="spinner-border spinner-border-sm" /> Saving...</> : "Save Changes"}</button>
        </footer>
      </form>
    </section>

    <section className="account-profile-card">
      <header className="account-profile-card-header"><div><h2>Change password</h2><p>Choose a secure password that you do not use elsewhere.</p></div></header>
      {passwordError && <div className="account-profile-alert is-error" role="alert">{passwordError}</div>}
      {passwordSuccess && <ToastFeedback type="success" message={passwordSuccess} />}
      <form onSubmit={savePassword} noValidate>
        <div className="account-profile-grid">
          {[
            ["currentPassword", "Current password", "current-password"],
            ["newPassword", "New password", "new-password"],
            ["confirmNewPassword", "Confirm new password", "new-password"]
          ].map(([field, label, autoComplete]) => <PasswordField
            key={field}
            id={`account-${field}`}
            label={label}
            value={password[field]}
            autoComplete={autoComplete}
            visible={Boolean(passwordVisibility[field])}
            onChange={(value) => setPassword((current) => ({ ...current, [field]: value }))}
            onToggle={() => setPasswordVisibility((current) => ({ ...current, [field]: !current[field] }))}
          />)}
        </div>
        <p className="account-profile-help account-password-help">Use 10 to 72 characters, including at least one letter and one number.</p>
        <footer className="account-profile-actions">
          <button type="button" className="account-profile-button is-secondary" disabled={savingPassword} onClick={() => { setPassword(emptyPassword); setPasswordError(""); setPasswordSuccess(""); }}>Clear</button>
          <button type="submit" className="account-profile-button is-primary" disabled={savingPassword}>{savingPassword ? <><span className="spinner-border spinner-border-sm" /> Updating...</> : "Update Password"}</button>
        </footer>
      </form>
    </section>
  </section>;
};

export default AccountProfile;
